import { create } from 'zustand'

/**
 * Device connection state here is a MIRROR of lib/device-store, which is the
 * single source of truth — it owns the device list and performs the actual
 * connect / ping / disconnect. This store only caches the flag so playback UI
 * can read it without subscribing to the whole device store.
 *
 * Two bugs used to live at this boundary:
 *
 *  1. device-store pushed updates via `setDeviceConnected` / `setDeviceLatency`,
 *     neither of which existed here. All 14 call sites were wrapped in
 *     `typeof fn === 'function'` guards, so every one silently did nothing and
 *     connection state never propagated. Both methods are now implemented below;
 *     the guards in device-store are redundant but harmless.
 *
 *  2. This store used `persist` to save `isDeviceConnected` to localStorage. A
 *     BLE/WebSocket connection cannot survive a reload, so the flag came back
 *     `true` against a device that wasn't there — which is why the booru modal's
 *     XY pad auto-opened on load while its own controls stayed disabled.
 *     device-store already marks every persisted device disconnected on rehydrate;
 *     mirroring that value has to be runtime-only, so `persist` is gone entirely
 *     (device status was the only thing it was persisting).
 */

export interface PlaybackControls {
  pause: () => void
  resume: () => void
  stop: () => void
}

interface ScriptPlaybackState {
  // Active script info
  activeScriptId: string | null
  activeScriptName: string | null
  isPlaying: boolean

  // Book script specific
  isBookScript: boolean
  currentPageIndex: number
  totalPages: number

  // Timing
  currentTime: number // seconds
  totalDuration: number
  scriptStartTime: number // timestamp when script started

  // Device status — mirrored from device-store, never authoritative here
  connectedDeviceName: string | null
  isDeviceConnected: boolean
  deviceLatencyMs: number
  lastCommandTimestamp: number

  // Active controls registered by whichever player is running
  controls: PlaybackControls | null
  registerControls: (controls: PlaybackControls) => void
  unregisterControls: () => void

  // Actions
  startScript: (scriptId: string, scriptName: string, isBookScript: boolean, totalDuration: number, totalPages?: number) => void
  stopScript: () => void
  pauseScript: () => void
  resumeScript: () => void
  updateTime: (time: number) => void
  updatePage: (pageIndex: number) => void
  updateDeviceStatus: (connected: boolean, deviceName: string | null) => void
  /** Called by device-store when only the connected flag is known. */
  setDeviceConnected: (connected: boolean) => void
  /** Called by device-store after each ping / latency measurement. */
  setDeviceLatency: (latencyMs: number) => void
  updateLastCommand: () => void
}

export const useScriptPlaybackStore = create<ScriptPlaybackState>()(
    (set, get) => ({
      // Initial state
      activeScriptId: null,
      activeScriptName: null,
      isPlaying: false,

      isBookScript: false,
      currentPageIndex: 0,
      totalPages: 0,

      currentTime: 0,
      totalDuration: 0,
      scriptStartTime: 0,

      connectedDeviceName: null,
      isDeviceConnected: false,
      deviceLatencyMs: 0,
      lastCommandTimestamp: 0,

      controls: null,
      registerControls: (controls) => set({ controls }),
      unregisterControls: () => set({ controls: null }),

      // Actions
      startScript: (scriptId, scriptName, isBookScript, totalDuration, totalPages = 0) => {
        console.log('[ScriptPlaybackStore] Starting script:', scriptName)
        set({
          activeScriptId: scriptId,
          activeScriptName: scriptName,
          isPlaying: true,
          isBookScript,
          totalPages,
          totalDuration,
          currentTime: 0,
          currentPageIndex: 0,
          scriptStartTime: Date.now(),
        })
      },

      stopScript: () => {
        console.log('[ScriptPlaybackStore] Stopping script')
        set({
          activeScriptId: null,
          activeScriptName: null,
          isPlaying: false,
          isBookScript: false,
          currentPageIndex: 0,
          totalPages: 0,
          currentTime: 0,
          totalDuration: 0,
          scriptStartTime: 0,
          controls: null,
        })
      },

      pauseScript: () => {
        console.log('[ScriptPlaybackStore] Pausing script')
        set({ isPlaying: false })
      },

      resumeScript: () => {
        console.log('[ScriptPlaybackStore] Resuming script')
        set({ isPlaying: true })
      },

      updateTime: (time) => {
        set({ currentTime: time })
      },

      updatePage: (pageIndex) => {
        console.log('[ScriptPlaybackStore] Updating page:', pageIndex)
        set({ currentPageIndex: pageIndex })
      },

      updateDeviceStatus: (connected, deviceName) => {
        set({
          isDeviceConnected: connected,
          connectedDeviceName: deviceName
        })
      },

      // Mirrors from device-store. Kept deliberately narrow: the flag and the
      // name are set independently because device-store knows the connected
      // state in several places where it doesn't have the name to hand.
      setDeviceConnected: (connected) => {
        set({ isDeviceConnected: connected })
      },

      setDeviceLatency: (latencyMs) => {
        set({ deviceLatencyMs: latencyMs })
      },

      updateLastCommand: () => {
        set({ lastCommandTimestamp: Date.now() })
      },
}))
